import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { DeviceMobile, X, AppleLogo, GooglePlayLogo, ArrowRight } from "@phosphor-icons/react";

const DISMISS_KEY = "tf_open_in_app_dismissed";
// Routes where surfacing the "open in app" prompt makes sense (high-value
// share targets). Add more here as needed.
const APP_SURFACE_ROUTES = [/^\/activity\/[^/]+$/, /^\/explore$/, /^\/leaderboard$/];

// Optional store URLs. Empty strings = button is hidden (app not published yet).
// Wire them in /app/frontend/.env once the apps are live.
const IOS_URL = process.env.REACT_APP_IOS_APP_URL || "";
const ANDROID_URL = process.env.REACT_APP_ANDROID_APP_URL || "";
// Custom-scheme fallback. Universal-Link path-matching takes over once
// AASA + assetlinks.json are filled in with Apple Team ID + SHA-256.
const APP_SCHEME = process.env.REACT_APP_APP_SCHEME || "teamfit";

function detectPlatform() {
  const ua = (typeof navigator !== "undefined" ? navigator.userAgent : "").toLowerCase();
  if (/iphone|ipod|ipad/.test(ua)) return "ios";
  if (/android/.test(ua)) return "android";
  return "desktop";
}

/**
 * OpenInAppBanner — mobile-only bottom sheet that surfaces when a user
 * lands on a high-value page (activity detail / explore / leaderboard).
 *
 * Behaviour:
 *  - Hidden on desktop and inside the native mobile app (detected via
 *    a 'TeamFitMobile' UA hint set by the React Native WebView, if any).
 *  - Dismissed per-session via sessionStorage (so it doesn't stack up
 *    across page-loads within one session, but reappears tomorrow).
 *  - Primary CTA tries the custom scheme first, then falls back to the
 *    store URL if the app didn't take focus within 1.2s (visibilitychange
 *    proxy).
 *  - "Continue on web" silently dismisses.
 */
export default function OpenInAppBanner() {
  const { lang } = useI18n();
  const { pathname } = useLocation();
  const [platform] = useState(detectPlatform);
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  // Eligibility check: re-run whenever the route changes.
  useEffect(() => {
    setVisible(false);
    if (platform === "desktop") return;
    // Bail out inside the native app's webview.
    const ua = (navigator.userAgent || "").toLowerCase();
    if (ua.includes("teamfitmobile") || ua.includes("teamfit-app")) return;
    // Surface only on whitelisted routes.
    if (!APP_SURFACE_ROUTES.some((re) => re.test(pathname))) return;
    // Honour session dismissal.
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === "1") {
        setDismissed(true);
        return;
      }
    } catch { /* ignore */ }
    // Tiny delay so it animates IN after the page settles.
    const t = setTimeout(() => setVisible(true), 350);
    return () => clearTimeout(t);
  }, [pathname, platform]);

  if (platform === "desktop" || dismissed || !visible) return null;

  const storeUrl = platform === "ios" ? IOS_URL : ANDROID_URL;
  const hasStore = Boolean(storeUrl);

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  const openInApp = () => {
    // Try the custom scheme. The native app, if installed, intercepts and
    // hands off. If nothing intercepts within 1.2s, fall back to the
    // platform store (when configured) or just dismiss the prompt.
    const path = pathname.startsWith("/") ? pathname.slice(1) : pathname;
    const deepLink = `${APP_SCHEME}://${path}`;
    const t0 = Date.now();
    const fallback = setTimeout(() => {
      // If the tab is still focused, the app likely isn't installed.
      if (Date.now() - t0 < 2000 && !document.hidden) {
        if (hasStore) window.location.href = storeUrl;
        else dismiss();
      }
    }, 1200);
    const cleanup = () => {
      if (document.hidden) clearTimeout(fallback);
    };
    document.addEventListener("visibilitychange", cleanup, { once: true });
    window.location.href = deepLink;
  };

  const title = lang === "da"
    ? "Få den fulde oplevelse i appen"
    : "Get the full experience in the app";
  const sub = lang === "da"
    ? "Push-notifikationer, hurtigere chat og live-lokation."
    : "Push notifications, faster chat, and live location.";
  const openLbl = lang === "da" ? "Åbn i appen" : "Open in app";
  const webLbl = lang === "da" ? "Fortsæt på web" : "Continue on web";

  return (
    <div
      className="fixed left-0 right-0 bottom-16 z-[60] px-4 pointer-events-none animate-fade-up"
      data-testid="open-in-app-banner"
    >
      <div className="pointer-events-auto max-w-md mx-auto bg-[#0A0A0A] border border-[#FFCC00]/40 shadow-2xl">
        <div className="flex items-start gap-3 p-4">
          <div className="w-10 h-10 bg-[#FFCC00]/15 border border-[#FFCC00]/40 flex items-center justify-center shrink-0">
            <DeviceMobile weight="duotone" size={20} color="#FFCC00" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold leading-tight" data-testid="open-in-app-title">{title}</div>
            <p className="text-[12px] text-zinc-400 mt-0.5 leading-snug">{sub}</p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            data-testid="open-in-app-dismiss"
            aria-label={lang === "da" ? "Luk" : "Close"}
            className="-mr-1 -mt-1 p-1.5 text-zinc-500 hover:text-white transition-colors"
          >
            <X weight="bold" size={14} />
          </button>
        </div>
        <div className="grid grid-cols-2 border-t border-[#262626]">
          <button
            type="button"
            onClick={dismiss}
            data-testid="open-in-app-continue-web"
            className="py-3 text-[11px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-white/[0.04] border-r border-[#262626]"
          >
            {webLbl}
          </button>
          <button
            type="button"
            onClick={openInApp}
            data-testid="open-in-app-cta"
            className="py-3 text-[11px] font-bold uppercase tracking-widest bg-[#FFCC00] hover:bg-[#e6b800] text-black flex items-center justify-center gap-2"
          >
            {platform === "ios"
              ? <AppleLogo weight="fill" size={14} />
              : <GooglePlayLogo weight="fill" size={14} />}
            {openLbl}
            <ArrowRight weight="bold" size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}
